import { subInboxVersionMs } from './subInboxVersion.ts';

export type MessageInboxStateKind = 'thread' | 'classPost' | 'classNote' | 'board' | 'bulletin' | 'sub';

export interface MessageInboxStateDoc {
  id?: string;
  kind: MessageInboxStateKind;
  itemId: string;
  filedThroughMs: number | null;
  readThroughMs: number | null;
  /** Exact normalized family note text for a real classNote row. An
   *  attendance concern filing rides the classNote kind too and stores the
   *  rule that fired here (lib/attendanceConcernFilingCore.ts). */
  filedThroughContent?: string | null;
  readThroughContent?: string | null;
  /** Personal conversation favorite. Only used by thread state rows. */
  pinned?: boolean;
  updatedAt?: unknown;
}

export interface ThreadInboxActivityLike {
  lastMessageAt?: unknown;
  lastMessageBy?: string | null;
  lastInboundAtByUid?: Record<string, unknown> | null;
  /** ThreadDoc.kind ('dm' | 'group'), read by threadUnreadCount.ts alongside
   *  `adminBoard` below: a real Admin board thread is always `kind ===
   *  'group'`, and requiring both mirrors the server's own
   *  `isAdminBoardThread` (adminBoardAccess.mjs) so a forged `adminBoard:
   *  true` on an ordinary DM (the thread-create rule is a blacklist and
   *  does not block that field) cannot be silenced by the admin board mute
   *  (REFUTE R1 finding 1). */
  kind?: string | null;
  /** Server-owned Admin board group marker (ThreadDoc.adminBoard), read by
   *  threadUnreadCount.ts to honor a fully muted admin board (2026-09-16
   *  morning batch item 2). Absent or false on every other thread kind. */
  adminBoard?: boolean | null;
}

export interface MessageInboxSourceLike extends ThreadInboxActivityLike {
  createdAt?: unknown;
  lastReplyAt?: unknown;
  updatedAt?: unknown;
  editedAt?: unknown;
  resentAt?: unknown;
  familyNote?: unknown;
  /* A sub request's own three fields, which decide when a filing expires (see
     the 'sub' branch of messageInboxSourceVersionMs). Declared rather than
     cast, so the source really is described here and a future rename of any of
     them fails the typecheck instead of silently reading undefined and filing
     the card forever. */
  status?: string;
  date?: string;
  claimedBy?: string | null;
}

export interface MessageInboxStateWrite {
  kind: MessageInboxStateKind;
  itemId: string;
  expectedVersionMs: number;
  /** Required for classNote. Exact text avoids collisions and unrelated offering edits. */
  expectedContent?: string;
}

/**
 * The one sentence for "something newer than what you acted on is sitting in
 * this conversation, so it did not file". Named because two callers need the
 * exact same words and only one of them throws: the transaction below raises
 * MessageInboxStateConflictError, while a card filing through its own message
 * (lib/notificationInboxFiling.ts) never throws and reports the sentence back
 * instead. One sentence, one place, so the two can never drift apart.
 */
export const MESSAGE_INBOX_STATE_CONFLICT_MESSAGE =
  'A new message arrived while you were filing this conversation. It stayed in your inbox.';

export class MessageInboxStateConflictError extends Error {
  constructor() {
    super(MESSAGE_INBOX_STATE_CONFLICT_MESSAGE);
    this.name = 'MessageInboxStateConflictError';
  }
}

/**
 * The backing thread, class post or announcement is gone, so there is no
 * cursor left to move. Named rather than a bare Error purely so a caller can
 * tell "nothing to file" apart from "the filing was refused": the first is
 * quiet, the second has to be said out loud. The wording is unchanged from
 * the bare Error it replaces, so every screen that prints error.message reads
 * exactly as it did before.
 */
export class MessageInboxSourceGoneError extends Error {
  constructor() {
    super('That message is no longer available.');
    this.name = 'MessageInboxSourceGoneError';
  }
}

export function timestampLikeMs(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, value);
  if (!value || typeof value !== 'object') return 0;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return 0;
  try {
    const result = toMillis.call(value);
    return typeof result === 'number' && Number.isFinite(result) ? Math.max(0, result) : 0;
  } catch {
    return 0;
  }
}

/**
 * The latest message activity that was inbound for this viewer. A reply sent
 * by the viewer does not advance this value, so it cannot reopen their own
 * filed conversation or make it unread again.
 *
 * D7 (AUDIT_D_messages_boards.md), tried and DROPPED (REFUTE_msg2_R6.md P1;
 * see WORKER_msg2_R1_REPORT.md's round seven section for the full account):
 * a read-side shortcut here once treated the newest inbound message as "no
 * activity at all" whenever the thread's denormalized preview text was
 * exactly RECALLED_THREAD_PREVIEW_TEXT, to stop a recalled message from
 * bolding a thread forever (recall cannot roll `lastMessageAt`,
 * `lastMessageBy` or `lastInboundAtByUid` back - the deployed rule only
 * lets a recall's write touch `lastMessageText`,
 * `changingOnly(['lastMessageText'])` in `isValidLastMessagePreviewUpdate()`
 * - so those three fields still name the recalled message after it is
 * gone). But this function has ONLY the thread document, never the actual
 * messages, so it cannot tell "the newest message was recalled and nothing
 * else is unread" apart from "the newest message was recalled and an
 * EARLIER inbound message is still genuinely unread": a thread with two
 * unread inbound messages whose newer one gets recalled read as having no
 * activity at all under that shortcut, hiding the still-unread earlier
 * message from the Messages row, the home card and the app badge alike -
 * worse than the stale bold it was built to fix, because it hides real
 * work rather than merely over-reporting old work. No surface that reads
 * this predicate loads the thread's actual messages until AFTER it has
 * already decided the thread is unread (`useThreadUnreadCounts` only fetches
 * once `threadUnreadSinceMs` is already non-null), so there was no already-
 * loaded data this function could fall back on. Dropped rather than papered
 * over: this function is back to reading only `lastInboundAtByUid`/
 * `lastMessageAt`/`lastMessageBy`, the same as before D7 existed, so a
 * thread goes back to staying bold after a recall until a new inbound
 * message arrives - the literal fix (rolling the three fields back in the
 * same transaction) still needs the rules change D7's own queue item asks
 * Bobbie for; nothing here papers over that gap or half-fixes it.
 */
export function threadInboundActivityMs(
  thread: ThreadInboxActivityLike,
  viewerUid: string
): number {
  const mapped = timestampLikeMs(thread.lastInboundAtByUid?.[viewerUid]);
  if (mapped > 0) return mapped;
  return thread.lastMessageBy && thread.lastMessageBy !== viewerUid
    ? timestampLikeMs(thread.lastMessageAt)
    : 0;
}

/**
 * A conversation is answered for this viewer while their own message is the
 * newest valid message. A later inbound message changes lastMessageBy and
 * immediately returns the conversation to Inbox. The inbound comparison is a
 * fail-open guard for malformed denormalized thread data.
 */
export function threadAnsweredByViewer(
  thread: ThreadInboxActivityLike,
  viewerUid: string
): boolean {
  if (!viewerUid || thread.lastMessageBy !== viewerUid) return false;
  const latestMessageMs = timestampLikeMs(thread.lastMessageAt);
  if (latestMessageMs <= 0) return false;
  return latestMessageMs >= threadInboundActivityMs(thread, viewerUid);
}

export function messageInboxSourceVersionMs(
  kind: MessageInboxStateKind,
  source: MessageInboxSourceLike,
  viewerUid: string
): number {
  if (kind === 'thread') return threadInboundActivityMs(source, viewerUid);
  if (kind === 'classPost') {
    // editedAt folded in (Packet R1, hub-staging TASK-QUEUE.md): the client
    // side (Messages.tsx's classPostActivityMs, via classPostFeedActivityMs
    // in messagesDoneFlow.ts) already includes editedAt in expectedVersionMs.
    // This transaction-side recompute must agree, or every edited post
    // guarantees an assertExpectedInboxVersion mismatch on the next filing
    // attempt (client sends the edited max, server recomputes without it),
    // which is a false "a new message arrived" conflict, not a race.
    return Math.max(
      timestampLikeMs(source.createdAt),
      timestampLikeMs(source.lastReplyAt),
      timestampLikeMs(source.editedAt)
    );
  }
  if (kind === 'board' || kind === 'bulletin') {
    // Receipt writes change updatedAt too, so only content activity reopens
    // a personally filed post. A new reply, edit or resend must remain new.
    return Math.max(
      timestampLikeMs(source.createdAt),
      timestampLikeMs(source.lastReplyAt),
      timestampLikeMs(source.editedAt),
      timestampLikeMs(source.resentAt)
    );
  }
  if (kind === 'sub') {
    /* THE MISSING BRANCH THAT BROKE "DONE WITH THIS" ON A SUB REQUEST.
       Without it this fell through to `return 0` while the caller passed the
       real subInboxVersionMs, and assertExpectedInboxVersion compares the two
       with STRICT equality - so every filing threw, on every sub request,
       since the button shipped. Found by Codex's review of PR #1003, and it is
       the write half of the same bug Bobbie reported live ("These aren't
       clearing even after I push the new Done with this button"); #1003 fixed
       only the Main-versus-Past read.

       Same arithmetic the row and the writer already use, from the one module
       that owns it, so the filing still expires at the 48 hour re-open mark
       and an uncovered class cannot sit filed away until it is too late. */
    return subInboxVersionMs(source, timestampLikeMs(source.updatedAt), Date.now());
  }
  // A class note is versioned by its own exact text, not offering.updatedAt.
  // Roster, teacher, or season edits must not reopen a filed note.
  return 0;
}

export function classNoteContentVersion(source: Pick<MessageInboxSourceLike, 'familyNote'>): string {
  return typeof source.familyNote === 'string' ? source.familyNote.trim() : '';
}

export function messageInboxStateId(kind: MessageInboxStateKind, itemId: string): string {
  return `${kind}__${encodeURIComponent(itemId)}`;
}

export function messageInboxStateKey(kind: MessageInboxStateKind, itemId: string): string {
  return `${kind}:${itemId}`;
}

export function isFiledThrough(
  state: Pick<MessageInboxStateDoc, 'filedThroughMs'> | null | undefined,
  currentVersionMs: number
): boolean {
  return typeof state?.filedThroughMs === 'number'
    && Number.isFinite(state.filedThroughMs)
    && state.filedThroughMs >= Math.max(0, currentVersionMs);
}

export function isReadThrough(
  state: Pick<MessageInboxStateDoc, 'readThroughMs'> | null | undefined,
  currentVersionMs: number
): boolean {
  return typeof state?.readThroughMs === 'number'
    && Number.isFinite(state.readThroughMs)
    && state.readThroughMs >= Math.max(0, currentVersionMs);
}

export function isClassNoteFiledThrough(
  state: Pick<MessageInboxStateDoc, 'filedThroughContent'> | null | undefined,
  currentContent: string
): boolean {
  return typeof state?.filedThroughContent === 'string'
    && state.filedThroughContent === currentContent.trim();
}

export function isClassNoteReadThrough(
  state: Pick<MessageInboxStateDoc, 'readThroughContent'> | null | undefined,
  currentContent: string
): boolean {
  return typeof state?.readThroughContent === 'string'
    && state.readThroughContent === currentContent.trim();
}

/**
 * Refuse a stale UI action instead of filing through activity that arrived
 * after the viewer tapped Done. Both values come from Firestore timestamps,
 * so equality is the only safe success case.
 */
export function assertExpectedInboxVersion(
  currentVersionMs: number,
  expectedVersionMs: number
): void {
  if (!Number.isFinite(expectedVersionMs)
      || Math.max(0, currentVersionMs) !== Math.max(0, expectedVersionMs)) {
    throw new MessageInboxStateConflictError();
  }
}

export function assertExpectedClassNoteContent(
  currentContent: string,
  expectedContent: string | undefined
): void {
  if (typeof expectedContent !== 'string' || currentContent.trim() !== expectedContent.trim()) {
    throw new MessageInboxStateConflictError();
  }
}
